import { Geotab } from "@fairfleet/geotab";
import { AssetFormValues, buildNewDeviceEntity, buildSerialNumberEntity, buildUpdatedDeviceEntity } from "./assetEntity";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";

/** The only calls this page makes that write to the database. */
type WriteClient = Pick<Geotab, "call" | "get">;

/** Set replaces the whole entity, so it starts from the device as it is now, not from a cached copy. */
async function getFreshDevice(client: WriteClient, id: string): Promise<DeviceWithVehicleInfo> {
  const [fresh] = (await client.get("Device", { id }, 1)) as DeviceWithVehicleInfo[];
  if (fresh?.id !== id) throw new Error(`Device ${id} could not be loaded`);
  return fresh;
}

/** Sets an existing device (full entity from a fresh Get right before) or adds a new one; returns the saved entity. */
export async function saveAsset(
  client: WriteClient,
  { existingId, form }: { existingId: string | null; form: AssetFormValues },
): Promise<DeviceWithVehicleInfo> {
  if (existingId) {
    const updatedDevice = buildUpdatedDeviceEntity(await getFreshDevice(client, existingId), form);
    await client.call("Set", { typeName: "Device", entity: updatedDevice });
    return updatedDevice;
  }

  const newDevice = buildNewDeviceEntity(form);
  const addedId = await client.call("Add", { typeName: "Device", entity: newDevice });
  return { ...newDevice, id: typeof addedId === "string" ? addedId : "" } as DeviceWithVehicleInfo;
}

/** Swaps the serial number of a device; `undefined` removes it. */
export async function setDeviceSerialNumber(
  client: WriteClient,
  deviceId: string,
  nextSerialNumber: string | undefined,
): Promise<void> {
  const device = await getFreshDevice(client, deviceId);
  await client.call("Set", { typeName: "Device", entity: buildSerialNumberEntity(device, nextSerialNumber) });
}
